import React from 'react';
import { withFormik } from 'formik';
import * as Yup from 'yup';

import styles from './MemberForm.module.scss';

import { Button, Input, Label, FormGroup, FormFeedback } from 'reactstrap';
import Cohort from './Cohort';
import { Icon } from 'js/components';
import { noop } from 'lodash';

const AddMemberForm = ({
  values,
  errors,
  text,
  handleChange,
  setFieldValue,
  handleBlur,
  handleReset,
  touched,
  isSubmitting,
  handleSubmit,
  cohorts,
  onCancel = noop,
}) => {
  const formikAttributes = (fieldName) => ({
    id: fieldName,
    name: fieldName,
    value: values[fieldName],
    onChange: handleChange,
    onBlur: handleBlur,
    invalid: errors[fieldName] && touched[fieldName],
  });

  const formikFeedback = (fieldName) => {
    return errors[fieldName] && touched[fieldName] ? (
      <FormFeedback>{errors[fieldName]}</FormFeedback>
    ) : null;
  };

  const handleCancel = () => {
    handleReset();
    onCancel();
  };

  return (
    <form onSubmit={handleSubmit} className={`form ${styles['member-card']}`}>
      <div className={styles['form-header']}>
        <h5>
          <small className="text-muted">
            {text ?? 'Who is part of this group?'}
          </small>
        </h5>
      </div>
      <div className={styles['member-avatar']}>
        <div className={styles['member-avatar-image']}>
          <Icon icon="user" size="lg" />
        </div>
      </div>
      <div className={styles['form-body']}>
        <div className={styles['member-name']}>
          <Label for="name">Name</Label>
          <FormGroup>
            <Input type="text" {...formikAttributes('name')} autoFocus />
            {formikFeedback('name')}
          </FormGroup>
        </div>
        <div className={styles['member-email']}>
          <Label for="email">Email</Label>
          <FormGroup>
            <Input type="text" {...formikAttributes('email')} />
            {formikFeedback('email')}
          </FormGroup>
        </div>
        <div className={styles['member-cohort']}>
          <Label for="cohort">Cohort</Label>
          <Cohort
            placeholder="(None)"
            list={cohorts}
            includeStandardRoles={true}
            onChange={(value) => setFieldValue('cohort', value)}
            value={values['cohort']}
          />
        </div>
        <div className={styles['member-title']}>
          <Label for="title">Title</Label>
          <FormGroup>
            <Input type="text" {...formikAttributes('title')} />
            {formikFeedback('title')}
          </FormGroup>
        </div>
        <div className={styles['member-actions']}>
          <Button color="primary" disabled={isSubmitting} type="submit">
            <Icon icon="plus" /> Add member
          </Button>
          <Button
            color="secondary"
            disabled={isSubmitting}
            onClick={handleCancel}
          >
            Cancel
          </Button>
        </div>
      </div>
    </form>
  );
};

const FormikAddMemberForm = withFormik({
  mapPropsToValues: ({ member = {} }) => {
    const { name = null, title = null, cohort = null, email = null } = member;

    return {
      name: name ?? '',
      title: title ?? '',
      cohort: cohort ?? '',
      email: email ?? '',
    };
  },
  validationSchema: Yup.object().shape({
    email: Yup.string()
      .trim()
      .required("Our robots can't deliver assessments without an email address")
      .email("Hmm, that doesn't look like a valid email address yet")
      .max(64),
    name: Yup.string()
      .trim()
      .required(
        "I've been through the desert on a horse with no name, but all your members need one"
      )
      .max(
        128,
        "That's a long name you got there. Unfortunately we cut you off at 128 characters."
      ),
  }),
  handleSubmit: async (values, bag) => {
    const {
      setSubmitting,
      props: { onAddMember = () => {} },
    } = bag;
    // do nothing
    setSubmitting(true);
    try {
      await onAddMember(values);
    } finally {
      setSubmitting(false);
    }
  },
  displayName: 'AddMember',
})(AddMemberForm);

export default FormikAddMemberForm;
